import countryCodesData from '../data/country-codes.json';
import {
  resolveDialCodeForCountryInput,
  resolveCountryNameForInput,
  resolveCountryIsoForInput,
} from '../utils/phoneValidation';

/** Detect ISO region from browser locale (e.g. en-SG → SG). */
export function detectBrowserCountryIso() {
  if (typeof navigator === 'undefined') return '';

  const candidates = [
    ...(navigator.languages || []),
    navigator.language,
  ].filter(Boolean);

  for (const lang of candidates) {
    const raw = String(lang).trim();
    const match = raw.match(/^[a-z]{2}-([A-Z]{2})$/i) || raw.match(/-([A-Z]{2})$/i);
    if (match?.[1]) return match[1].toUpperCase();
  }

  return '';
}

function countryMetaFromDialCode(dialCode, countryCodes = countryCodesData) {
  if (!dialCode) return null;
  return countryCodes.find((c) => c.dialCode === dialCode) || null;
}

/**
 * Resolve phone dial code + country for NexaStay guest forms.
 *
 * Priority:
 * 1. Saved country_code from guest profile (DB)
 * 2. Saved country ISO/name from guest profile (DB)
 * 3. Visitor country from booking engine (IP geolocation via hotel API)
 * 4. Browser locale region
 * 5. Hotel property country
 * 6. India (+91) default
 */
export function resolveGuestPhoneLocale({
  savedCountryCode = '',
  savedCountry = '',
  visitorCountry = '',
  visitorCountryIso = '',
  hotelCountry = '',
  countryCodes = countryCodesData,
  guestCountries = null,
} = {}) {
  const savedDial = resolveDialCodeForCountryInput(savedCountryCode, countryCodes, guestCountries);
  const savedCountryDial = resolveDialCodeForCountryInput(savedCountry, countryCodes, guestCountries);
  const visitorDial = resolveDialCodeForCountryInput(
    visitorCountryIso || visitorCountry,
    countryCodes,
    guestCountries
  );
  const browserIso = detectBrowserCountryIso();
  const browserDial = resolveDialCodeForCountryInput(browserIso, countryCodes, guestCountries);
  const hotelDial = resolveDialCodeForCountryInput(hotelCountry, countryCodes, guestCountries);

  const dialCode =
    savedDial ||
    savedCountryDial ||
    visitorDial ||
    browserDial ||
    hotelDial ||
    '+91';

  const dialMeta = countryMetaFromDialCode(dialCode, countryCodes);

  let countryIso = '';
  if (savedDial && dialMeta?.code) {
    countryIso = dialMeta.code;
  } else if (savedCountry) {
    countryIso = resolveCountryIsoForInput(savedCountry, guestCountries);
  } else if (visitorCountryIso) {
    countryIso = String(visitorCountryIso).trim().toUpperCase();
  } else if (visitorCountry) {
    countryIso = resolveCountryIsoForInput(visitorCountry, guestCountries);
  } else if (browserIso) {
    countryIso = browserIso;
  } else if (hotelCountry) {
    countryIso = resolveCountryIsoForInput(hotelCountry, guestCountries);
  } else {
    countryIso = dialMeta?.code || 'IN';
  }

  const countryName =
    resolveCountryNameForInput(countryIso, guestCountries) ||
    (dialMeta?.name || '') ||
    resolveCountryNameForInput(savedCountry, guestCountries) ||
    resolveCountryNameForInput(visitorCountry, guestCountries) ||
    resolveCountryNameForInput(hotelCountry, guestCountries) ||
    '';

  return { dialCode, countryName, countryIso };
}

/** Build initial guest locale when loading a saved profile. */
export function resolveGuestPhoneLocaleFromProfile(profile, options = {}) {
  return resolveGuestPhoneLocale({
    savedCountryCode: profile?.country_code,
    savedCountry: profile?.country,
    visitorCountry: options.visitorCountry,
    visitorCountryIso: options.visitorCountryIso,
    hotelCountry: options.hotelCountry,
    countryCodes: options.countryCodes,
    guestCountries: options.guestCountries,
  });
}
